/**
 * This component is used to display the organization setup page.
 */
import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, RouterLink } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { FlexLayoutModule } from 'ngx-flexible-layout';
import { NgSelectModule } from '@ng-select/ng-select';
import { Loader, Loading } from '../loader/loader';
import { CommonService } from '../../services/common.service';
import { ToastService } from '../../../shared/services/toast-service';
import { CompanySettingService } from '../../../settings/services/company-setting.service';
import { AuthService } from '../../../auth/services/auth.service';
import { ApiResponse, IdName } from '../../models/common.model';
import { ToastType } from '../../../shared/models/toast.model';
import { CustomerDetails } from '../../../settings/models/company-setting.modals';
/**
 * This component is used to display the organization setup page.
 */
@Component({
  selector: 'app-organization-setup',
  imports: [CommonModule, ReactiveFormsModule, FlexLayoutModule, NgSelectModule, Loader, RouterLink],
  templateUrl: './organization-setup.html',
  styleUrl: './organization-setup.scss',
})
export class OrganizationSetup implements OnInit {
  /**
   * Variable which is used to hold all subscriptions for cleanup on component destroy.
   */
  private readonly subscriptions: Subscription = new Subscription();
  /**
   * Variable which is used to store all client messages such as error, success, warning, info, etc.
   */
  public messages: Record<string, string> = {};
  /**
   * Variable which is used to define whether to display the loading or not.
   */
  public readonly loading: Loading = { button: false, page: true };
  /**
   * Variable which is used to create reactive forms for adding the customer details.
   */
  public orgForm!: UntypedFormGroup;
  /**
   * Variable which is used to store the data retrieved from countries JSON file.
   */
  public countries: string[] = [];
  /**
   * Variable which is used to store the data retrieved from states JSON file.
   */
  public states: string[] = [];
  /**
   * Variable which is used to store the data retrieved from customer table.
   */
  public customerDetails!: CustomerDetails;
  /**
   * Variable which is used to store all the industry details.
   */
  public industryDetails: IdName[] = [];
  /**
   * Variable which is used to store the timezone details.
   */
  public timezone: IdName[] = [];
  /**
   * Component constructor which is used to inject the required services.
   * @param router To manage navigation.
   * @param authService To access the functions inside the AuthService.
   * @param toastService To access the functions inside the ToastService.
   * @param companySettingService To access the functions inside the CompanySettingService.
   * @param commonService To access the functions inside the CommonService.
   */
  constructor(
    private router: Router,
    private authService: AuthService,
    private toastService: ToastService,
    private companySettingService: CompanySettingService,
    private commonService: CommonService) { }
  /**
   * Lifecycle hook that is called once after component initialization.
   */
  public ngOnInit(): void {
    this.messages = this.authService.errorMessages;
    forkJoin({
      customerDetails: this.companySettingService.getCustomerDetails(),
      industryDetails: this.companySettingService.getIndustryDetails(),
      timezoneDetails: this.companySettingService.getTimezoneDetails()
    }).subscribe({
      next: (res: any) => {
        this.customerDetails = res.customerDetails.data;
        this.industryDetails = res.industryDetails.data;
        this.timezone = res.timezoneDetails.data;
        this.formInitialise();
        this.loading.page = false;
      },
      error: (err: HttpErrorResponse) => {
        if (err) {
          console.log('Error: ', err.error.error.message);
          this.toastService.show({ type: ToastType.Error, message: this.messages.FAILED_TO_FETCH });
          this.loading.page = false;
        }
      }
    });
  }
  /**
   * Method which is used to initialize the form.
   */
  private formInitialise(): void {
    this.orgForm = new FormGroup({
      name: new FormControl(this.customerDetails.name ?? null, Validators.required),
      industryId: new FormControl(null, Validators.required),
      address: new FormControl(null, Validators.required),
      city: new FormControl(null, Validators.required),
      country: new FormControl(null, Validators.required),
      state: new FormControl(null, Validators.required),
      zipcode: new FormControl(null, Validators.required),
      timezoneId: new FormControl(null, Validators.required)
    });
    this.subscriptions.add(this.commonService.countries.subscribe(response => this.countries = response));
    this.orgForm.get('country')?.valueChanges.subscribe(country => this.getStates(country));
  }
  /**
   * Method which is used to get the state details.
   * @param country To define the name of the country.
   */
  public getStates(country: string): void {
    this.orgForm.get('state')?.reset();
    this.states = this.commonService.getStates(country);
  }
  /**
   * Method which is used to save the customer details.
   */
  public onSave(): void {
    if (this.orgForm.valid) {
      this.loading.button = true;
      this.companySettingService.editCustomerDetails(this.orgForm.value).subscribe({
        next: (res: ApiResponse<null>) => {
          if (res.success) {
            this.toastService.show({ type: ToastType.Success, message: this.messages.ORGANIZATION_SETUP_UPDATE });
            this.orgForm.markAsPristine();
            this.router.navigate(['/app/home']);
          }
        },
        error: (err: HttpErrorResponse) => {
          this.loading.button = false;
          if (err && err.error && err.error.error && err.error.error.message) {
            console.log('Error: ', err.error.error.message);
            this.toastService.show({ type: ToastType.Error, message: err.error.error.message });
          }
        }
      });
    } else {
      this.orgForm.markAllAsTouched();
    }
  }
  /**
   * Component OnDestroy life cycle hook.
   * And unsubscribe all the subscriptions in the component.
   */
  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
